import { Close, ContentCopy, Search } from '@mui/icons-material';
import {
  Chip,
  darken,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  fieldTemplateByIdAndSearch,
  ToastSeverity,
  toastStateSelector,
} from 'state';

export default function FieldTemplateChipList({
  parentAlias,
}: {
  parentAlias: string;
}) {
  const setToast = useSetRecoilState(toastStateSelector);
  const [query, setQuery] = useState('');
  const templates =
    useRecoilValue(
      fieldTemplateByIdAndSearch({
        alias: parentAlias,
        search: query,
      }),
    ) ?? [];

  const { t } = useTranslation();

  const onCopyToClipboard = async (alias: string) => {
    try {
      // Try the modern Clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(alias);
      } else {
        // Fallback for older browsers or when Clipboard API fails
        const textArea = document.createElement('textarea');
        textArea.value = alias;

        // Make the textarea out of viewport
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);

        textArea.focus();
        textArea.select();

        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);

        if (!successful) {
          throw new Error('Copy command was unsuccessful');
        }
      }

      setToast({
        isOpen: true,
        message: t('forms.aliasCopied'),
        severity: ToastSeverity.success,
      });
    } catch (error) {
      console.error('Failed to copy text: ', error);
      setToast({
        isOpen: true,
        message: t('forms.aliasCopyError'),
        severity: ToastSeverity.error,
      });
    }
  };

  return (
    <Box
      display="flex"
      flexDirection={'column'}
      width={'100%'}
      p={1}
      borderRadius={1}
      sx={{
        '--Grid-borderWidth': '1px',
        border: 'var(--Grid-borderWidth) solid',
        borderColor: 'divider',
      }}
    >
      <Typography
        sx={{
          fontSize: '14px',
          mb: 2,
          whiteSpace: 'nowrap',
          color: theme => theme.palette.action.active,
        }}
      >
        {t('forms.aliasesOfFields')}:
      </Typography>
      <TextField
        size="small"
        variant="outlined"
        sx={{
          width: '300px',
          mb: 1,
        }}
        label={t('general.search')}
        value={query}
        onChange={e => setQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {query && (
                <IconButton
                  size="small"
                  onClick={() => {
                    setQuery('');
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              )}
            </InputAdornment>
          ),
        }}
      />

      <Box display="flex" flexWrap="nowrap" overflow={'auto'} pb={1}>
        {templates.map(template => (
          <Chip
            variant="filled"
            key={template.id}
            color="primary"
            label={template.alias}
            icon={<ContentCopy fontSize="small" sx={{ fontSize: '12px' }} />}
            onClick={() => onCopyToClipboard(template.alias)}
            sx={{
              mr: 1,
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: theme =>
                  darken(theme.palette.primary.main, 0.2),
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
}
